// /opt/isp_erp/frontend/src/pages/tickets/CreateRetiroTicket.jsx
import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Grid,
  Alert,
  Snackbar,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Paper,
  List,
  ListItem,
  ListItemText,
  Chip,
  Divider,
  InputAdornment,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton as MuiIconButton,
  Autocomplete  // ✅ NUEVO: para el buscador
} from '@mui/material';
import {
  Send, Cancel, Warning, Person, Add, Delete,
  ExpandMore, Inventory
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { ticketsAPI } from '../../api/tickets.api';
import ClienteBuscador from '../../components/ClienteBuscador';
import { warehouseAPI } from '../../api/warehouse.api';
import api from '../../api/axios';
import { scheduleAPI } from '../../api/schedule.api';

const CreateRetiroTicket = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [cargandoDatos, setCargandoDatos] = useState(true);
  const [clientes, setClientes] = useState([]);
  const [tecnicos, setTecnicos] = useState([]);
  const [equiposInstalados, setEquiposInstalados] = useState([]);
  const [selectedCliente, setSelectedCliente] = useState(null);
  const [selectedEquipos, setSelectedEquipos] = useState([]);
  const [expandedEquipos, setExpandedEquipos] = useState(false);
  const [selectedTecnico, setSelectedTecnico] = useState(null);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  // Estados para verificación de disponibilidad
  const [checkingAvailability, setCheckingAvailability] = useState(false);
  const [isAvailable, setIsAvailable] = useState(null);
  const [availabilityError, setAvailabilityError] = useState('');
  const [availableSlots, setAvailableSlots] = useState([]);

  const [formData, setFormData] = useState({
    customer_id: '',
    assigned_technician_id: '',
    motivo_retiro: '',
    title: '',
    description: '',
    priority: 'media',
    contact_phone: '',
    contact_phone_alt: '',
    scheduled_date: ''
  });

  // ✅ NUEVO: clientes filtrados (solo RETIRADOS)
  const clientesRetirados = React.useMemo(() => {
    return clientes.filter(c => {
      const estado = c.estado || '';
      return estado === 'RETIRADO' || estado === 'retirado';
    });
  }, [clientes]);

  useEffect(() => {
    cargarDatos();
  }, []);

  useEffect(() => {
    if (formData.customer_id) {
      const cliente = clientes.find(c => c.id === parseInt(formData.customer_id));
      setSelectedCliente(cliente || null);
    } else {
      setSelectedCliente(null);
      setSelectedEquipos([]);
    }
  }, [formData.customer_id, clientes]);

  useEffect(() => {
    if (formData.assigned_technician_id) {
      const tecnico = tecnicos.find(t => t.id === parseInt(formData.assigned_technician_id));
      setSelectedTecnico(tecnico);
      setIsAvailable(null);
      setAvailabilityError('');
      setAvailableSlots([]);
    } else {
      setSelectedTecnico(null);
    }
  }, [formData.assigned_technician_id, tecnicos]);

  // El cliente se busca en el servidor mientras se escribe; sus equipos se piden solo cuando se elige
  const elegirCliente = (cliente) => {
    setClientes(cliente ? [cliente] : []);
    setEquiposInstalados([]);
    if (cliente) {
      api.get(`/customers/${cliente.id}/equipos-instalados`)
        .then((r) => setEquiposInstalados(r.data || []))
        .catch(() => setEquiposInstalados([]));
    }
    setFormData(prev => ({
      ...prev,
      customer_id: cliente?.id || '',
      contact_phone: cliente?.telefono || ''
    }));
  };

  const cargarDatos = async () => {
    try {
      const [usuariosResponse] = await Promise.all([
        api.get('/users')
      ]);

      const todosUsuarios = usuariosResponse.data || [];
      setTecnicos(todosUsuarios);

    } catch (error) {
      console.error('Error cargando datos:', error);
      setError('Error al cargar datos necesarios');
    } finally {
      setCargandoDatos(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const agregarEquipo = (equipo) => {
    if (!selectedEquipos.find(e => e.sn === equipo.sn)) {
      setSelectedEquipos([...selectedEquipos, {
        sn: equipo.sn,
        motivo: ''
      }]);
    }
  };

  const eliminarEquipo = (index) => {
    setSelectedEquipos(selectedEquipos.filter((_, i) => i !== index));
  };

  const actualizarMotivoEquipo = (index, motivo) => {
    const nuevosEquipos = [...selectedEquipos];
    nuevosEquipos[index].motivo = motivo;
    setSelectedEquipos(nuevosEquipos);
  };

  const checkAvailability = async (dateTime) => {
    if (!formData.assigned_technician_id || !dateTime) {
      setIsAvailable(null);
      return;
    }

    setCheckingAvailability(true);
    setAvailabilityError('');
    setIsAvailable(null);
    setAvailableSlots([]);

    try {
      const result = await scheduleAPI.checkAvailability(
        formData.assigned_technician_id,
        dateTime,
        60
      );

      setIsAvailable(result.available);

      if (!result.available) {
        setAvailabilityError('El técnico no está disponible en este horario');
        loadAvailableSlots(dateTime);
      }
    } catch (err) {
      console.error('Error checking availability:', err);
      setAvailabilityError('Error al verificar disponibilidad');
    } finally {
      setCheckingAvailability(false);
    }
  };

  const loadAvailableSlots = async (dateTime) => {
    if (!formData.assigned_technician_id || !dateTime) return;

    try {
      const result = await scheduleAPI.getAvailableSlots(
        formData.assigned_technician_id,
        dateTime,
        60
      );
      setAvailableSlots(result.available_slots || []);
    } catch (err) {
      console.error('Error loading available slots:', err);
    }
  };

  const handleDateTimeChange = (e) => {
    const dateTime = e.target.value;
    setFormData(prev => ({ ...prev, scheduled_date: dateTime }));
    if (dateTime && formData.assigned_technician_id) {
      checkAvailability(dateTime);
    } else {
      setIsAvailable(null);
      setAvailabilityError('');
      setAvailableSlots([]);
    }
  };

  const selectAlternativeSlot = (slot) => {
    const dateTime = new Date(slot.start_time);
    const formattedDateTime = dateTime.toISOString().slice(0, 16);
    setFormData(prev => ({ ...prev, scheduled_date: formattedDateTime }));
    checkAvailability(formattedDateTime);
  };

  const equiposDelCliente = equiposInstalados.filter(
    e => e.customer_id === parseInt(formData.customer_id)
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.customer_id) {
      setError('Debe seleccionar un cliente');
      return;
    }
    if (selectedEquipos.length === 0) {
      setError('Debe seleccionar al menos un equipo a retirar');
      return;
    }
    if (!formData.assigned_technician_id) {
      setError('Debe seleccionar el técnico que realizará el retiro');
      return;
    }
    if (!formData.motivo_retiro && selectedEquipos.every(e => !e.motivo)) {
      setError('Debe ingresar un motivo para el retiro');
      return;
    }
    if (!formData.contact_phone) {
      setError('Debe ingresar un teléfono de contacto');
      return;
    }
    if (formData.scheduled_date && isAvailable !== true) {
      setError('Debe seleccionar un horario disponible para el técnico');
      return;
    }

    setLoading(true);
    setError('');

    try {
      await ticketsAPI.createRetiroTicket({
        customer_id: parseInt(formData.customer_id),
        equipos: selectedEquipos,
        assigned_technician_id: parseInt(formData.assigned_technician_id),
        motivo_retiro: formData.motivo_retiro,
        title: formData.title || `Retiro de equipos - ${selectedCliente?.nombres} ${selectedCliente?.apellidos}`,
        description: formData.description,
        priority: formData.priority,
        contact_phone: formData.contact_phone,
        contact_phone_alt: formData.contact_phone_alt || null,
        scheduled_date: formData.scheduled_date || null
      });

      setSuccess(true);
      setTimeout(() => {
        navigate('/tickets');
      }, 2000);
    } catch (err) {
      console.error('Error creating ticket:', err);
      setError(err.response?.data?.detail || 'Error al crear el ticket de retiro');
    } finally {
      setLoading(false);
    }
  };

  if (cargandoDatos) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <CircularProgress />
        <Typography sx={{ mt: 2 }}>Cargando datos...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Ticket de Retiro de Equipos
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Crear una orden de trabajo para retirar uno o más equipos de casa del cliente
      </Typography>

      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <form onSubmit={handleSubmit}>
                <Grid container spacing={3}>
                  {/* ✅ CAMBIO: Autocomplete con búsqueda + solo clientes RETIRADOS */}
                  <Grid item xs={12} md={6}>
                    <ClienteBuscador
                      value={selectedCliente}
                      onChange={elegirCliente}
                      estados="RETIRADO"
                      label="Cliente *"
                      helperText="Solo se muestran clientes en estado RETIRADO"
                      required
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth required>
                      <InputLabel>Técnico Asignado</InputLabel>
                      <Select
                        name="assigned_technician_id"
                        value={formData.assigned_technician_id}
                        onChange={handleChange}
                        label="Técnico Asignado"
                      >
                        <MenuItem value="">Seleccione un técnico</MenuItem>
                        {tecnicos.map(tecnico => (
                          <MenuItem key={tecnico.id} value={tecnico.id}>
                            {tecnico.nombre || tecnico.username || tecnico.email}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Teléfono de Contacto"
                      name="contact_phone"
                      value={formData.contact_phone}
                      onChange={handleChange}
                      required
                      placeholder="Ej: 0999123456"
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Teléfono Alternativo"
                      name="contact_phone_alt"
                      value={formData.contact_phone_alt}
                      onChange={handleChange}
                      placeholder="Opcional"
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                      <InputLabel>Prioridad</InputLabel>
                      <Select
                        name="priority"
                        value={formData.priority}
                        onChange={handleChange}
                        label="Prioridad"
                      >
                        <MenuItem value="baja">Baja</MenuItem>
                        <MenuItem value="media">Media</MenuItem>
                        <MenuItem value="alta">Alta</MenuItem>
                        <MenuItem value="critica">Crítica</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      type="datetime-local"
                      label="Fecha Programada"
                      name="scheduled_date"
                      value={formData.scheduled_date}
                      onChange={handleDateTimeChange}
                      InputLabelProps={{ shrink: true }}
                      disabled={!formData.assigned_technician_id}
                      helperText={!formData.assigned_technician_id ? "Primero seleccione un técnico" : ""}
                      error={!!availabilityError}
                    />

                    {checkingAvailability && (
                      <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                        <CircularProgress size={16} sx={{ mr: 1 }} />
                        <Typography variant="caption">Verificando disponibilidad...</Typography>
                      </Box>
                    )}

                    {isAvailable === true && !checkingAvailability && formData.scheduled_date && (
                      <Alert severity="success" sx={{ mt: 1, py: 0 }}>
                        ✅ Técnico disponible en este horario
                      </Alert>
                    )}

                    {availabilityError && (
                      <Alert severity="error" sx={{ mt: 1, py: 0 }}>
                        {availabilityError}
                      </Alert>
                    )}

                    {availableSlots.length > 0 && (
                      <Box sx={{ mt: 1 }}>
                        <Typography variant="caption" gutterBottom display="block">
                          Horarios disponibles sugeridos:
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {availableSlots.slice(0, 3).map((slot, idx) => (
                            <Chip
                              key={idx}
                              label={new Date(slot.start_time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                              onClick={() => selectAlternativeSlot(slot)}
                              color="primary"
                              variant="outlined"
                              size="small"
                              clickable
                            />
                          ))}
                        </Box>
                      </Box>
                    )}
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Motivo General del Retiro (opcional)"
                      name="motivo_retiro"
                      value={formData.motivo_retiro}
                      onChange={handleChange}
                      multiline
                      rows={2}
                      placeholder="Motivo general si aplica a todos los equipos..."
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Título"
                      name="title"
                      value={formData.title}
                      onChange={handleChange}
                      required
                      placeholder="Ej: Retiro de equipos por cancelación"
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Descripción"
                      name="description"
                      multiline
                      rows={3}
                      value={formData.description}
                      onChange={handleChange}
                      placeholder="Detalles adicionales del retiro..."
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Paper sx={{ p: 2, bgcolor: '#fff3e0' }}>
                      <Typography variant="body2" color="warning.main">
                        <strong>⚠️ Importante:</strong> Al cerrar este ticket, los equipos cambiarán a estado
                        <strong> RETIRO_PENDIENTE</strong> y quedarán bajo responsabilidad del técnico.
                        Deberán ser devueltos a bodega.
                      </Typography>
                    </Paper>
                  </Grid>

                  <Grid item xs={12}>
                    <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                      <Button
                        variant="outlined"
                        onClick={() => navigate('/tickets')}
                        startIcon={<Cancel />}
                      >
                        Cancelar
                      </Button>
                      <Button
                        type="submit"
                        variant="contained"
                        disabled={loading || !formData.customer_id || selectedEquipos.length === 0 || !formData.assigned_technician_id || (formData.scheduled_date && isAvailable !== true)}
                        startIcon={loading ? <CircularProgress size={20} /> : <Send />}
                      >
                        {loading ? 'Creando...' : 'Crear Ticket'}
                      </Button>
                    </Box>
                  </Grid>
                </Grid>
              </form>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              {/* Sección de Equipos a Retirar */}
              <Accordion expanded={expandedEquipos} onChange={() => setExpandedEquipos(!expandedEquipos)}>
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Typography variant="h6">
                    <Inventory sx={{ mr: 1, verticalAlign: 'middle' }} />
                    Equipos a Retirar ({selectedEquipos.length})
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  {selectedCliente && (
                    <Paper sx={{ p: 2, mb: 2, bgcolor: '#f5f5f5' }}>
                      <Typography variant="subtitle2">Cliente seleccionado:</Typography>
                      <Typography variant="body2">
                        {selectedCliente.nombres} {selectedCliente.apellidos}
                      </Typography>
                      <Chip
                        label={`Estado: ${selectedCliente.estado || 'N/A'}`}
                        size="small"
                        color="error"
                        sx={{ mt: 0.5 }}
                      />
                    </Paper>
                  )}

                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Equipos instalados del cliente:
                  </Typography>
                  <List dense sx={{ maxHeight: 200, overflow: 'auto', mb: 2 }}>
                    {!formData.customer_id ? (
                      <ListItem><ListItemText primary="Seleccione un cliente primero" /></ListItem>
                    ) : equiposDelCliente.length === 0 ? (
                      <ListItem><ListItemText primary="No hay equipos instalados para este cliente" /></ListItem>
                    ) : (
                      equiposDelCliente.map(equipo => (
                        <ListItem
                          key={equipo.id}
                          button
                          onClick={() => agregarEquipo(equipo)}
                          disabled={selectedEquipos.some(e => e.sn === equipo.sn)}
                        >
                          <ListItemText
                            primary={equipo.sn}
                            secondary={equipo.modelo}
                          />
                          {selectedEquipos.some(e => e.sn === equipo.sn) && (
                            <Chip label="Agregado" color="success" size="small" />
                          )}
                        </ListItem>
                      ))
                    )}
                  </List>

                  <Divider sx={{ my: 2 }} />

                  <Typography variant="subtitle2">Equipos seleccionados:</Typography>
                  {selectedEquipos.length === 0 ? (
                    <Typography variant="caption" color="text.secondary">Ningún equipo seleccionado</Typography>
                  ) : (
                    selectedEquipos.map((eq, idx) => (
                      <Paper key={idx} sx={{ p: 2, mb: 2 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                          <Typography variant="body2" fontWeight="bold">{eq.sn}</Typography>
                          <MuiIconButton size="small" color="error" onClick={() => eliminarEquipo(idx)}>
                            <Delete fontSize="small" />
                          </MuiIconButton>
                        </Box>
                        <TextField
                          fullWidth
                          size="small"
                          label="Motivo del retiro"
                          value={eq.motivo}
                          onChange={(e) => actualizarMotivoEquipo(idx, e.target.value)}
                          placeholder="Ej: Cliente canceló servicio"
                        />
                      </Paper>
                    ))
                  )}
                </AccordionDetails>
              </Accordion>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Snackbar open={success} autoHideDuration={3000} onClose={() => setSuccess(false)} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert severity="success">Ticket de retiro creado exitosamente</Alert>
      </Snackbar>

      <Snackbar open={!!error} autoHideDuration={6000} onClose={() => setError('')} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert severity="error">{error}</Alert>
      </Snackbar>
    </Box>
  );
};

export default CreateRetiroTicket;
